import { useMemo, useState } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, X } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectField,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  TextField,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import {
  AddProductsDialog,
  type PickedProduct,
  type PickedPricingEntry,
} from "../collections/add-products-dialog.tsx";
import { ModeCardRow, type ModeOption } from "../../../components/mode-card.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { shortDay } from "../date-window.ts";
import { ComingSoonButton } from "./coming-soon-button.tsx";
import { exactMoney } from "./quote-format.ts";

/** A line in the editor: the picked product plus how many packs, and which. */
interface DraftLine extends PickedProduct {
  /** Number of **packs** (spec 13 R7). */
  qty: number;
  /** Which pricing entry is selected; falls back to the primary. */
  packId: string | undefined;
}

/**
 * The two kinds, in the order an operator meets them (spec 16 R1, design 18).
 * Same card-radio the collection form uses for core/seasonal.
 */
const KIND_OPTIONS: ReadonlyArray<ModeOption<"at_once" | "prebook">> = [
  { id: "at_once", title: "At-once", sub: "From stock. Ships now." },
  { id: "prebook", title: "Pre-book", sub: "Books an upcoming season for later delivery." },
];

/**
 * Only the header fields are under react-hook-form. `lines` stays `useState`
 * deliberately: it is an editable grid fed by a dialog picker, and moving it to
 * `useFieldArray` would be a large rewrite of code no acceptance criterion asks
 * us to touch. "Add at least one product" stays a toast because it is not a
 * field — there is nothing to hang an inline message on.
 */
const quoteSchema = z
  .object({
    customerId: z.string().min(1, "Pick a buyer"),
    kind: z.enum(["at_once", "prebook"]),
    sourceCollectionId: z.string().trim(),
    deliveryStartsOn: z.string().trim(),
    deliveryEndsOn: z.string().trim(),
    expiresOn: z.string().trim(),
    coverMessage: z.string().trim().max(2000),
  })
  .superRefine((v, ctx) => {
    // Mirrors the server's `createInput` refinement (spec 16 R2) so the operator
    // sees it on the field instead of as a toast after a round-trip. At-once is
    // normalised server-side, so there is nothing to check on that branch.
    if (v.kind !== "prebook") return;
    if (!v.sourceCollectionId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceCollectionId"],
        message: "Pick the season being booked",
      });
    }
    // On BOTH blank paths: one message on `start` leaves the end field looking
    // fine while the form silently refuses to submit.
    if (!v.deliveryStartsOn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deliveryStartsOn"],
        message: "Pre-book needs a delivery window",
      });
    }
    if (!v.deliveryEndsOn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deliveryEndsOn"],
        message: "Pre-book needs a delivery window",
      });
    }
    if (v.deliveryStartsOn && v.deliveryEndsOn && v.deliveryEndsOn < v.deliveryStartsOn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deliveryEndsOn"],
        message: "End date is before start",
      });
    }
  });

type QuoteFormValues = z.infer<typeof quoteSchema>;

/** The entry a line is priced from — the named pack, else the primary. */
function entryFor(l: DraftLine): PickedPricingEntry | undefined {
  if (l.packId) return l.pricingEntries.find((e) => e.packId === l.packId);
  return l.pricingEntries[0];
}

/**
 * The **pack** price, matching what `createQuote` computes server-side.
 *
 * A product with no priced entry shows ₹0.00 rather than a blank: it is visible
 * in the Summary before the operator saves, where a dash would hide the problem.
 */
function unitPaise(l: DraftLine): bigint {
  const e = entryFor(l);
  return e?.pricePaise ? BigInt(e.pricePaise) : 0n;
}

const TEXTAREA_CLASS =
  "w-full rounded border border-line bg-paper px-3 py-2 text-sm text-ink " +
  "placeholder:text-muted focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

/**
 * Store admin — New quote (Commerce, M4; pre-book per spec 16).
 *
 * Totals shown here are computed client-side for feedback only. The server
 * re-prices every line from the catalogue on save and stores its own arithmetic
 * — a price the browser could influence is not a price you can send a buyer.
 *
 * @see prototype/store/admin/all.html#quotes-new · docs/spec/18-store-prebook-orders-design.md
 */
export function QuoteNewPage() {
  const navigate = useNavigate();
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      customerId: "",
      kind: "at_once",
      sourceCollectionId: "",
      deliveryStartsOn: "",
      deliveryEndsOn: "",
      expiresOn: "",
      coverMessage: "",
    },
  });

  const kind = useWatch({ control: form.control, name: "kind" });
  const isPrebook = kind === "prebook";

  const buyers = trpc.customers.list.useQuery({ limit: 100 });
  // Only seasons that haven't opened yet can be booked (spec 16 R3). Not fetched
  // at all for an at-once quote — there is nothing to choose.
  const seasons = trpc.storeCollections.list.useQuery(
    { limit: 100, phase: "upcoming" },
    { enabled: isPrebook },
  );
  const seasonItems = seasons.data?.items ?? [];
  const noSeasons = isPrebook && !seasons.isLoading && seasonItems.length === 0;

  const create = trpc.storeQuotes.create.useMutation({
    onError: (e) => toast.error("Couldn't save the quote", { description: storeErrorMessage(e) }),
  });
  const send = trpc.storeQuotes.transition.useMutation({
    onError: (e) => toast.error("Saved, but couldn't send", { description: storeErrorMessage(e) }),
  });

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + unitPaise(l) * BigInt(l.qty), 0n),
    [lines],
  );

  function addPicked(_ids: string[], picked: PickedProduct[]) {
    setLines((prev) => {
      const have = new Set(prev.map((l) => l.listingId));
      return [
        ...prev,
        ...picked
          .filter((p) => !have.has(p.listingId))
          // Defaults to the primary entry — the one the server would pick too.
          .map((p) => ({ ...p, qty: 1, packId: p.pricingEntries[0]?.packId })),
      ];
    });
    setPickerOpen(false);
  }

  async function submit(values: QuoteFormValues, thenSend: boolean) {
    if (lines.length === 0) {
      toast.error("Add at least one product");
      return;
    }
    const result = await create.mutateAsync({
      customerId: values.customerId,
      kind: values.kind,
      expiresOn: values.expiresOn || null,
      coverMessage: values.coverMessage.trim() || null,
      // Stripped for at-once: the fields are hidden, so a stale value would be
      // invisible and still get saved. The server nulls them too, but sending
      // them would make the request say something the operator didn't.
      sourceCollectionId: values.kind === "prebook" ? values.sourceCollectionId : null,
      deliveryStartsOn: values.kind === "prebook" ? values.deliveryStartsOn || null : null,
      deliveryEndsOn: values.kind === "prebook" ? values.deliveryEndsOn || null : null,
      lines: lines.map((l) => ({
        listingId: l.listingId,
        qty: l.qty,
        ...(l.packId ? { packId: l.packId } : {}),
      })),
    });
    if (thenSend) {
      await send.mutateAsync({ quoteId: result.quoteId, status: "sent" });
      toast.success("Quote sent");
    } else {
      toast.success("Quote saved as draft");
    }
    await navigate({ to: "/store/admin/quotes/$quoteId", params: { quoteId: result.quoteId } });
  }

  const busy = create.isPending || send.isPending;

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Quotes", to: "/store/admin/quotes" },
        { label: "New quote" },
      ]}
    >
      {/* `grid`, despite being a create screen: the lines editor is a table
          beside the 320px rail, and the Pack column added for spec 16 left it
          with ~34px of slack at form width. The rail's own fields are narrow
          regardless. */}
      <StorePageColumn variant="grid">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-5">
          <div className="flex items-start gap-4">
            <BackButton fallbackTo="/store/admin/quotes" label="Back to quotes" className="mt-1" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
                New quote
              </h1>
              <p className="mt-1 text-sm text-muted">
                Line sheet + prices for a buyer, with expiry
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              intent="secondary"
              size="md"
              disabled={busy}
              onClick={() => void form.handleSubmit((v) => submit(v, false))()}
            >
              Save as draft
            </Button>
            <Button
              intent="primary"
              size="md"
              disabled={busy}
              onClick={() => void form.handleSubmit((v) => submit(v, true))()}
            >
              Send to buyer
            </Button>
          </div>
        </div>

        <Form {...form}>
          {/* Two submit paths, so the form element itself never submits — the
              buttons drive `handleSubmit`. Same shape as `collection-form.tsx`. */}
          <form onSubmit={(e) => e.preventDefault()}>
            <Card className="mt-5">
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Quote kind</h2>
              </CardHeader>
              <CardBody className="grid gap-4">
                <FormField
                  control={form.control}
                  name="kind"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ModeCardRow
                          value={field.value}
                          onChange={(next) => {
                            field.onChange(next);
                            // Switching back to At-once hides three fields. Their
                            // errors do NOT clear themselves, and an invisible
                            // error blocks every later submit with nothing on
                            // screen to fix — a genuine dead end.
                            if (next === "at_once") {
                              form.clearErrors([
                                "sourceCollectionId",
                                "deliveryStartsOn",
                                "deliveryEndsOn",
                              ]);
                            }
                          }}
                          options={KIND_OPTIONS}
                          // Its default is "Customer type" — forgetting this
                          // ships a radiogroup with the wrong accessible name.
                          ariaLabel="Quote kind"
                          className="sm:grid-cols-2 lg:grid-cols-2"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Mounted, not disabled: for an at-once quote these don't apply
                    rather than being temporarily unavailable. Values survive a
                    toggle because `shouldUnregister` is left off. */}
                {isPrebook ? (
                  <>
                    {seasons.isLoading ? (
                      <div className="grid gap-1.5">
                        <Label>Season</Label>
                        <Skeleton className="h-9 w-full" />
                      </div>
                    ) : noSeasons ? (
                      // Explained, never silent: the Pre-book card stays
                      // selectable and the dead end says how to leave it.
                      <Alert
                        intent="info"
                        title="No upcoming season to book."
                        description="Create a seasonal collection with a future window first."
                      >
                        <Button asChild intent="ghost" size="sm" className="mt-2.5">
                          <Link to="/store/admin/collections/new">New collection</Link>
                        </Button>
                      </Alert>
                    ) : (
                      <SelectField
                        name="sourceCollectionId"
                        label="Season *"
                        placeholder="Select the season…"
                        options={seasonItems.map((c) => ({
                          value: c.collectionId,
                          label: c.activeFrom
                            ? `${c.name} — opens ${shortDay(c.activeFrom)}`
                            : c.name,
                        }))}
                      />
                    )}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <TextField name="deliveryStartsOn" label="Delivery start *" type="date" />
                      <TextField name="deliveryEndsOn" label="Delivery end *" type="date" />
                    </div>
                  </>
                ) : null}
              </CardBody>
            </Card>

            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
              <Card>
                <CardHeader>
                  <h2 className="text-sm font-semibold text-ink">Quote lines</h2>
                </CardHeader>
                <CardBody>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-left text-sm">
                      <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Product</th>
                          <th className="px-3 py-2 text-left font-medium">Pack</th>
                          <th className="px-3 py-2 text-right font-medium">Qty (packs)</th>
                          <th className="px-3 py-2 text-right font-medium">Price / pack</th>
                          <th className="px-3 py-2 text-right font-medium">Total</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l, i) => {
                          const entry = entryFor(l);
                          const pieces = entry?.pieces ?? 0;
                          return (
                            <tr key={l.listingId} className="border-b border-line">
                              <td className="px-3 py-2 text-ink">
                                {l.name}
                                <span className="ml-2 font-mono text-xs text-muted">
                                  {l.sku ?? "—"}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                {l.pricingEntries.length === 0 ? (
                                  <span className="text-xs text-muted">—</span>
                                ) : l.pricingEntries.length === 1 ? (
                                  // One option is not a choice: a select here
                                  // would imply the operator could pick something
                                  // else.
                                  <span className="text-xs text-ink-2">
                                    {l.pricingEntries[0]!.label}
                                  </span>
                                ) : (
                                  <Select
                                    value={l.packId ?? ""}
                                    onValueChange={(packId) =>
                                      setLines((prev) =>
                                        prev.map((row, n) => (n === i ? { ...row, packId } : row)),
                                      )
                                    }
                                  >
                                    <SelectTrigger
                                      className="h-8"
                                      aria-label={`Pack for ${l.name}`}
                                    >
                                      <SelectValue placeholder="Pack…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {l.pricingEntries.map((e) => (
                                        <SelectItem key={e.packId ?? e.label} value={e.packId ?? ""}>
                                          {e.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Input
                                  type="number"
                                  min={1}
                                  value={l.qty}
                                  disabled={l.pricingEntries.length === 0}
                                  title={
                                    l.pricingEntries.length === 0
                                      ? "This product has no pack price yet"
                                      : undefined
                                  }
                                  aria-label={`Packs for ${l.name}`}
                                  className="h-8 w-20 text-right font-mono tabular-nums"
                                  onChange={(e) => {
                                    const qty = Math.max(1, Number(e.target.value) || 1);
                                    setLines((prev) =>
                                      prev.map((row, n) => (n === i ? { ...row, qty } : row)),
                                    );
                                  }}
                                />
                                {pieces > 0 ? (
                                  <div className="mt-0.5 text-xs text-muted tabular-nums">
                                    {l.qty * pieces} pcs
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-2">
                                {exactMoney(unitPaise(l).toString())}
                              </td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                                {exactMoney((unitPaise(l) * BigInt(l.qty)).toString())}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Button
                                  intent="ghost"
                                  size="sm"
                                  aria-label={`Remove ${l.name}`}
                                  onClick={() => setLines((prev) => prev.filter((_, n) => n !== i))}
                                >
                                  <X size={14} aria-hidden />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                        {lines.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-3 py-10 text-center text-sm text-muted">
                              No lines yet. Add a product to start the line sheet.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
                    <Button intent="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                      <Plus size={14} aria-hidden />
                      Add product
                    </Button>
                    {/* Bulk-adding a whole collection needs a rule for duplicates
                        and for what "the collection changed since" means — deferred
                        rather than half-built. */}
                    <ComingSoonButton
                      label="Add collection"
                      reason="Bulk add from a collection"
                      size="sm"
                    />
                  </div>
                </CardBody>
              </Card>

              <div className="grid gap-5 lg:content-start">
                <Card>
                  <CardHeader>
                    <h2 className="text-sm font-semibold text-ink">Buyer &amp; expiry</h2>
                  </CardHeader>
                  <CardBody className="grid gap-4">
                    <SelectField
                      name="customerId"
                      label="Buyer *"
                      placeholder="Select a buyer…"
                      options={(buyers.data?.items ?? []).map((b) => ({
                        value: b.id,
                        label: b.name,
                      }))}
                    />

                    <TextField
                      name="expiresOn"
                      label="Expires on"
                      type="date"
                      helper="Leave blank for a quote that never lapses."
                    />

                    <FormField
                      control={form.control}
                      name="coverMessage"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cover message</FormLabel>
                          <FormControl>
                            <textarea
                              {...field}
                              rows={3}
                              placeholder="Included on the PDF cover page"
                              className={TEXTAREA_CLASS}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <h2 className="text-sm font-semibold text-ink">Summary</h2>
                  </CardHeader>
                  <CardBody>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted">Subtotal</span>
                      <span className="font-mono tabular-nums text-ink">
                        {exactMoney(subtotal.toString())}
                      </span>
                    </div>
                    {/* Deliberately no GST line here: the tax rate is per item and
                        lives on the server. Showing a guessed figure that the saved
                        quote then contradicts would be worse than showing none. */}
                    <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
                      Estimated before discounts and GST — both are applied from each
                      product&rsquo;s own rates when the quote is saved.
                    </p>
                  </CardBody>
                </Card>
              </div>
            </div>
          </form>
        </Form>
      </StorePageColumn>

      <AddProductsDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        alreadyIn={lines.map((l) => l.listingId)}
        busy={false}
        onAdd={addPicked}
        title="Add products to quote"
      />
    </StoreAdminLayout>
  );
}
